// src/components/stages/ResolveStage.tsx
import { useState } from 'react';
import VoiceMicButton from '../VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '../VoiceFormFiller';
import type { StageProps } from '../../types/visit';
import { StageLayout } from '../ui/StageLayout';
import { StageActionBar } from '../ui/StageActionBar';

type ResolutionId = 'closed' | 'referred' | 'admitted' | 'followup';

interface ResolutionMeta {
  id: ResolutionId;
  label: string;
  icon: string;
  desc: string;
  color: string;
  text: string;
}

const RESOLUTIONS: ResolutionMeta[] = [
  { id:'closed',   label:'Case closed',   icon:'✅', desc:'Visit complete, no follow-up',        color:'#D1FAE5', text:'#065F46' },
  { id:'referred', label:'Referred',      icon:'↗',  desc:'Referred to specialist or hospital',  color:'#FEF3C7', text:'#92400E' },
  { id:'admitted', label:'Admitted (IP)', icon:'🛏',  desc:'Patient being admitted to ward',      color:'#DBEAFE', text:'#1D4ED8' },
  { id:'followup', label:'Follow-up',     icon:'📅',  desc:'Review appointment scheduled',        color:'#F3E8FF', text:'#5B21B6' },
];

const VOICE_FIELDS: VoiceField[] = [
  { key:'resolution', label:'Resolution', type:'radio', options: RESOLUTIONS.map(r => r.label) },
  { key:'resolution_notes', label:'Resolution notes', type:'text' },
];
const LABEL_TO_ID: Record<string, ResolutionId> = Object.fromEntries(RESOLUTIONS.map(r => [r.label, r.id]));

export default function ResolveStage({ visit, patient, completeStage, loading }: Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading'>) {
  const [res, setRes]   = useState<ResolutionId | null>(null);
  const [notes, setNotes] = useState('');
  const [followDate, setFD] = useState('');

  const applyVoiceFields = (extracted: Record<string, string>) => {
    const resolutionLabel = extracted.resolution;
    if (resolutionLabel && LABEL_TO_ID[resolutionLabel]) setRes(LABEL_TO_ID[resolutionLabel]!);
    if (extracted.resolution_notes) setNotes(prev => (prev ? prev + ' ' : '') + extracted.resolution_notes);
  };

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    <div className="card">
      <div style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>✅ Resolve visit</div>
      <div style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>Doctor marks final status for this visit</div>

      <VoiceFormFiller fields={VOICE_FIELDS} onFields={applyVoiceFields} label="🎙 Fill this by voice" />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:18 }}>
        {RESOLUTIONS.map(r=>(
          <div key={r.id} onClick={()=>setRes(r.id)}
            style={{ padding:'14px 12px', border:`2px solid ${res===r.id?r.text:'#E5E7EB'}`, background:res===r.id?r.color:'#fff', borderRadius:'var(--radius)', cursor:'pointer', display:'flex', alignItems:'center', gap:10, transition:'all .15s' }}>
            <span style={{ fontSize:22 }}>{r.icon}</span>
            <div>
              <div style={{ fontWeight:600, fontSize:13, color:res===r.id?r.text:'#111827' }}>{r.label}</div>
              <div style={{ fontSize:11, color:res===r.id?r.text:'#9CA3AF' }}>{r.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {res === 'followup' && (
        <div style={{ marginBottom:14 }}>
          <label className="lbl">Follow-up date</label>
          <input type="date" value={followDate} onChange={e=>setFD(e.target.value)} />
          <div style={{ fontSize:11, color:'#9CA3AF', marginTop:4 }}>
            The patient gets a reminder (SMS / WhatsApp) the day before this date. No date, no reminder.
          </div>
        </div>
      )}

      <div style={{ marginBottom:14 }}>
        <label className="lbl" style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span>Resolution notes</span>
          <VoiceMicButton onText={t => setNotes(prev => (prev ? prev + ' ' : '') + t)} />
        </label>
        <textarea style={{ resize:'vertical', height:80 }} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Additional notes, referral details, instructions…" />
      </div>

      <div style={{ padding:'12px 14px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', marginBottom:16, fontSize:13 }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>Visit summary</div>
        <div style={{ color:'#6B7280' }}>Patient: <b>{patient?.name || visit?.name || '—'}</b></div>
        <div style={{ color:'#6B7280' }}>Visit ID: <b>{visit?.visit_id}</b></div>
        <div style={{ color:'#6B7280' }}>Type: <b>{visit?.visit_type || 'OP'}</b></div>
      </div>

    </div>
    </StageLayout>
    <StageActionBar
      readiness={!res ? 'Choose a resolution to continue' : '✓ Ready to close visit'}
      readinessTone={!res ? 'warn' : 'success'}
    >
      <button className="btn-primary" disabled={!res||loading}
        onClick={()=>completeStage('resolve',{ resolution:res, resolution_notes:notes, followup_date:followDate },notes)}>
        Close visit →
      </button>
    </StageActionBar>
    </div>
  );
}
